import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { STATUS_LABEL_TO_KEY, getActiveStatuses } from '../utils/statusHelpers'

function UserProfile() {
  const { userId } = useParams()
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  
  const [profile, setProfile] = useState(null)
  const [stats, setStats] = useState(null)
  const [collection, setCollection] = useState([])
  const [loading, setLoading] = useState(true)
  const [collectionLoading, setCollectionLoading] = useState(false)
  const [error, setError] = useState('')
  const [activeStatus, setActiveStatus] = useState('Own')


  const isOwnProfile = currentUser?.id === userId

    // All users can now see all statuses (including "Own")
  const ALL_STATUSES = [
    'All',
    'Own',
    'For Trade',
    'Want in Trade',
    'Want to Buy',
    'Wishlist',
    'Pre-Ordered',
    'Previously Owned'
  ]

  // Everyone sees the same statuses - full transparency for community building
  const statusesToShow = ALL_STATUSES

  useEffect(() => {
    fetchProfile()
    fetchCollectionStats()
  }, [userId])

  useEffect(() => {
    fetchCollection()
  }, [userId, activeStatus])

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('public_user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) throw error

      setProfile(data)
      setLoading(false)
    } catch (error) {
      console.error('Error fetching profile:', error)
      setError('Profile not found or private')
      setLoading(false)
    }
  }

  const fetchCollectionStats = async () => {
    try {
      const { data, error } = await supabase
        .from('user_collection_summary')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') throw error

      console.log('Stats fetched:', data)
      setStats(data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const fetchCollection = async () => {
    setCollectionLoading(true)
    
    try {
      let query = supabase
        .from('user_lure_inventory')
        .select(`
          *,
          lure_variants (
            variant_id,
            variant_name,
            image_url,
            is_limited_edition,
            color_name,
            base_lures (
              base_lure_id,
              name,
              weight,
              length,
              brands (brand_name),
              lure_types (type_name)
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      // Apply status filter
      if (activeStatus !== 'All') {
        query = query.eq(STATUS_LABEL_TO_KEY[activeStatus], true)
      }

      const { data, error } = await query

      if (error) throw error

      setCollection(data || [])
      setCollectionLoading(false)
    } catch (error) {
      console.error('Error fetching collection:', error)
      setCollectionLoading(false)
    }
  }

  // ============================================
  // FIX: Proper status to database column mapping
  // ============================================
  const getStatusCount = (status) => {
    if (!stats) return 0
    
    if (status === 'All') {
      return stats.total_items || 0
    }
    
    // Map status names to actual database column names
    const statusMap = {
      'Own': 'owned_count',
      'Previously Owned': 'previously_owned_count',
      'For Trade': 'for_trade_count',
      'Want in Trade': 'want_in_trade_count',
      'Want to Buy': 'want_to_buy_count',
      'Pre-Ordered': 'pre_ordered_count',
      'Wishlist': 'wishlist_count'
    }
    
    const columnName = statusMap[status]
    return stats[columnName] || 0
  }


  if (loading) {
    return <div className="loading">Loading profile...</div>
  }

  if (error || !profile) {
    return (
      <div className="error-page">
        <h2>Profile Not Found</h2>
        <p>{error || 'This profile does not exist or is set to private'}</p>
        <Link to="/" className="btn-primary">← Back to Home</Link>
      </div>
    )
  }

  if (!profile.profile_is_public && !isOwnProfile) {
    return (
      <div className="error-page">
        <h2>Profile Private</h2>
        <p>This user has set their profile to private.</p>
        <Link to="/" className="btn-primary">← Back to Home</Link>
      </div>
    )
  }

  return (
    <div className="user-profile-page">
      <div className="profile-banner">
        <div className="profile-banner-content">
          <div className="profile-avatar">
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt={profile.username} />
            ) : (
              <div className="avatar-placeholder">
                {profile.username?.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          
          <div className="profile-info">
            <h1>{profile.username}</h1>
            {profile.full_name && (
              <p className="profile-full-name">{profile.full_name}</p>
            )}
            {profile.location && profile.show_location && (
              <p className="profile-location">📍 {profile.location}</p>
            )}
            <p className="profile-joined">
              Member since {new Date(profile.created_at).toLocaleDateString()}
            </p>
          </div>

          {isOwnProfile && (
            <Link to="/my-profile" className="btn-secondary">
              ✏️ Edit Profile
            </Link>
          )}
          {!isOwnProfile && currentUser && (
            <button
              className="btn-primary"
              onClick={() => navigate(`/messages?with=${userId}`)}
            >
              ✉️ Send Message
            </button>
          )}
        </div>
      </div>

      <div className="profile-content-grid">
        {/* Left Column */}
        <div className="profile-main">
          {/* Bio */}
          {profile.bio && (
            <div className="profile-section">
              <h2>About</h2>
              <p className="profile-bio">{profile.bio}</p>
            </div>
          )}

          {/* Contact */}
          {profile.public_email && profile.show_email && (
            <div className="profile-section">
              <h2>Contact</h2>
              <p>
                <strong>Email:</strong>{' '}
                <a href={`mailto:${profile.public_email}`}>
                  {profile.public_email}
                </a>
              </p>
            </div>
          )}

          {/* Collection with Status Tabs */}
          <div className="profile-section">
            <h2>{isOwnProfile ? 'My Collection' : `${profile.username}'s Collection`}</h2>
            
            {/* Status Tabs - FIXED VERSION */}
            <div className="profile-status-tabs">
              {statusesToShow.map(status => {
                const count = getStatusCount(status)
                
                return (
                  <button
                    key={status}
                    onClick={() => setActiveStatus(status)}
                    className={`profile-status-tab ${activeStatus === status ? 'active' : ''}`}
                  >
                    {status} ({count})
                  </button>
                )
              })}
            </div>

            {/* Collection Grid */}
            {collectionLoading ? (
              <div className="loading-small">Loading collection...</div>
            ) : collection.length === 0 ? (
              <div className="no-collection-items">
                <p>No items with status "{activeStatus}"</p>
              </div>
            ) : (
              <div className="profile-collection-grid">
                {collection.map(item => {
                  const variant = item.lure_variants
                  const baseLure = variant?.base_lures
                  
                  return (
                    <div key={item.inventory_id} className="profile-collection-card">
                      {/* Variant Image */}
                      {variant?.image_url && (
                        <div className="profile-item-image-container">
                          <img 
                            src={variant.image_url} 
                            alt={variant?.color_name}
                            className="profile-item-image"
                          />
                        </div>
                      )}

                      {/* Lure Name */}
                      <Link 
                        to={`/lure/${baseLure?.base_lure_id}`}
                        className="profile-item-name"
                      >
                        {baseLure?.name}
                      </Link>

                      {/* Color Badge */}
                      <div className="profile-item-color">
                        <span className="color-name-badge">
                          {variant?.variant_name || variant?.color_name}
                        </span>
                        {variant?.is_limited_edition && (
                          <span className="limited-badge-tiny">LE</span>
                        )}
                      </div>

                      {/* Details */}
                      <div className="profile-item-details">
                        <p><strong>Brand:</strong> {baseLure?.brands?.brand_name}</p>
                        <p><strong>Type:</strong> {baseLure?.lure_types?.type_name}</p>
                        <p><strong>Quantity:</strong> {item.quantity}</p>
                        {item.condition && (
                          <p><strong>Condition:</strong> {item.condition}</p>
                        )}
                      </div>

                      {/* Status Badge */}
                      <div className="profile-item-status">
                        {getActiveStatuses(item).map(s => (
                          <span key={s.key} className={`status-badge-small ${s.cssClass}`}>{s.label}</span>
                        ))}
                        {item.wishlist_priority && item.status_wishlist && (
                          <span className="priority-badge-small">
                            {item.wishlist_priority}
                          </span>
                        )}
                      </div>

                      {/* Notes (if viewing own profile or public) */}
                      {item.notes && isOwnProfile && (
                        <div className="profile-item-notes">
                          <strong>Notes:</strong> {item.notes}
                        </div>
                      )}

                      {/* Added Date */}
                      <div className="profile-item-footer">
                        <span className="profile-item-date">
                          Added {new Date(item.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="profile-sidebar">
          <div className="profile-stats-card">
            <h3>Collection Stats</h3>
            {stats ? (
              <>
                <div className="profile-stat">
                  <span className="profile-stat-number">{stats.total_items || 0}</span>
                  <span className="profile-stat-label">Total Items</span>
                </div>
                <div className="profile-stat">
                  <span className="profile-stat-number">{stats.owned_count || 0}</span>
                  <span className="profile-stat-label">Owned</span>
                </div>
                <div className="profile-stat">
                  <span className="profile-stat-number">{stats.for_trade_count || 0}</span>
                  <span className="profile-stat-label">For Trade</span>
                </div>
                <div className="profile-stat">
                  <span className="profile-stat-number">{stats.wishlist_count || 0}</span>
                  <span className="profile-stat-label">Wishlist</span>
                </div>
                <div className="profile-stat">
                  <span className="profile-stat-number">{stats.want_in_trade_count || 0}</span>
                  <span className="profile-stat-label">Want in Trade</span>
                </div>
              </>
            ) : (
              <p className="no-stats-text">No collection data</p>
            )}
          </div>

          {!isOwnProfile && profile.public_email && profile.show_email && (
            <div className="profile-contact-card">
              <h3>Contact for Trading</h3>
              <a href={`mailto:${profile.public_email}`} className="btn-primary btn-full">
                📧 Send Email
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserProfile